import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardContainer, MainContent, TopBar, PageContent, ContentSection } from './layout/AppLayout';
import AnalyticsSidebar from './layout/AnalyticsSidebar';
import DashboardLoadingShell from './layout/DashboardLoadingShell';
import logoImage from '../assets/images/STS_Logo.png';
import { normalizeRole } from './manageUsers.utils';
import { apiUrl } from '../api';
import { buildAuthHeaders } from './session.utils';
import { TablePrintButton } from './TablePrintButton';
import { PrintableTableReport } from './PrintableTableReport';
import { formatReportContext, matchesTableSearch, paginateTableRows } from './tableReporting.utils';
import { StudentProgressResetAction } from './StudentProgressLifecycleActions';
import '../styles/topachievers.css';

export default function AdminTopAchievers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [topAchievers, setTopAchievers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [refreshToken, setRefreshToken] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Load and apply theme
        const savedTheme = localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', savedTheme);

        const loggedInUser = JSON.parse(localStorage.getItem('loggedInUser'));
        if (!loggedInUser || normalizeRole(loggedInUser.role) !== 'admin') {
          navigate('/login');
          return;
        }

        setUser(loggedInUser);

        // Fetch top achievers data (admin sees all)
        const requestOptions = { headers: buildAuthHeaders() };
        const response = await fetch(apiUrl('/api/top-achievers'), requestOptions);
        if (response.ok) {
          const data = await response.json();
          setTopAchievers(Array.isArray(data) ? data : []);
        } else {
          setError('Failed to load top achievers');
        }
      } catch (err) {
        console.error('Error initializing component:', err);
        setError('Connection error');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [navigate, refreshToken]);

  const filteredAchievers = useMemo(() => topAchievers.filter((achiever) => matchesTableSearch(
    achiever,
    searchQuery,
    ['student_name', 'game_student_id', 'grade_level', 'section', 'completion_percentage', 'game_score', 'score', 'quests_completed']
  )), [searchQuery, topAchievers]);
  const paginatedAchievers = paginateTableRows(filteredAchievers, page, pageSize);

  useEffect(() => {
    if (page !== paginatedAchievers.currentPage) {
      setPage(paginatedAchievers.currentPage);
    }
  }, [page, paginatedAchievers.currentPage]);

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery;
  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return 'No Data';
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? `${numericValue.toFixed(1)}%` : 'No Data';
  };
  const formatCompletionPercent = (value) => (
    value === null || value === undefined || value === '' ? 'N/A' : formatPercent(value)
  );
  const completionValue = (row) => (
    Object.prototype.hasOwnProperty.call(row, 'completion_percentage')
      ? row.completion_percentage
      : row.progress_percentage
  );
  const metricPercentWidth = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.min(100, Math.max(0, numericValue)) : null;
  };
  const formatMetric = (value) => (value === null || value === undefined || value === '' ? '—' : value);
  const formatPlaytime = (seconds) => {
    const totalSeconds = Number(seconds || 0);
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return 'N/A';
    const minutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${Math.max(1, minutes)}m`;
  };
  const reportScope = searchQuery ? `Search: ${searchQuery}` : 'All authorised students';
  const reportColumns = [
    { header: '#', value: (_, index) => index + 1 },
    { header: 'Student ID', value: (row) => row.game_student_id },
    { header: 'Student Name', value: (row) => row.student_name },
    { header: 'Grade', value: (row) => row.grade_level },
    { header: 'Section', value: (row) => row.section },
    { header: 'Progress', value: (row) => formatCompletionPercent(completionValue(row)) },
    { header: 'Game Score', value: (row) => formatMetric(row.game_score ?? row.score) },
    { header: 'Correct / Total', value: (row) => `${formatMetric(row.total_correct_answers ?? row.correct_answers)}/${formatMetric(row.total_questions_answered ?? row.total_questions)}` },
    { header: 'Completed Quests', value: (row) => formatMetric(row.quests_completed ?? row.total_quests_completed) },
    { header: 'Playtime', value: (row) => formatPlaytime(row.total_play_time ?? row.duration_seconds) },
  ];

  if (loading) {
    return (
      <DashboardLoadingShell
        role="admin"
        activeItem="top-achievers"
        logoSrc={logoImage}
        portalLabel="Admin Portal"
        heading="Top Achievers"
        subheading="View student achievements and learning progress."
      />
    );
  }

  return (
    <DashboardContainer
      sidebar={
        <AnalyticsSidebar
          role="admin"
          activeItem="top-achievers"
          logoSrc={logoImage}
          portalLabel="Admin Portal"
        />
      }
      main={
        <MainContent>
          <TopBar>
            <div className="header-info">
              <h1>Top Achievers</h1>
              <p>Recognize students with exceptional performance across all grades and sections.</p>
            </div>
          </TopBar>

          <PageContent>
            <ContentSection>
              <div className="filters-section">
                <div className="filters-row">
                  <div className="filter-group">
                    <label>Search Top Achievers</label>
                    <input
                      type="search"
                      placeholder="Search name, ID, grade, section, progress, or game score..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>

                  <div className="filter-actions">
                    {hasActiveFilters && (
                      <button
                        className="btn-reset"
                        onClick={resetFilters}
                        type="button"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </ContentSection>

            <ContentSection title={`Top Achievers (${filteredAchievers.length} found)`}>
              <div className="table-report-controls">
                <StudentProgressResetAction
                  role="admin"
                  label="Reset Top Achievers"
                  warning="Resetting Top Achievers starts a new learning cycle for the affected students. Their current progress and leaderboard ranking will restart, while historical results and Screen Time remain preserved."
                  onComplete={() => setRefreshToken((value) => value + 1)}
                />
                <TablePrintButton
                  reportTitle="Top Achievers Report"
                  reportContext={formatReportContext({ scope: reportScope, recordCount: filteredAchievers.length })}
                  label="Print Top Achievers"
                  showPrintHeading={false}
                />
              </div>
              {error ? (
                <div className="error-message">{error}</div>
              ) : filteredAchievers.length === 0 ? (
                <>
                  <div className="empty-message">No leaderboard data available yet.</div>
                  <div className="pagination-row no-print" aria-label="Top Achievers pagination">
                    <span>0 records</span>
                    <button type="button" disabled>Previous</button>
                    <span>Page 1 of 1</span>
                    <button type="button" disabled>Next</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="top-achievers-container">
                  <table className="ta-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Student Name</th>
                        <th>Student ID</th>
                        <th>Grade</th>
                        <th>Section</th>
                        <th>Completion</th>
                        <th>Game Score</th>
                        <th>Correct Answers</th>
                        <th>Quests</th>
                        <th>Playtime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAchievers.rows.map((achiever, index) => {
                        const rank = paginatedAchievers.start + index;
                        const completion = completionValue(achiever);
                        const completionWidth = metricPercentWidth(completion);
                        const gameScore = achiever.game_score ?? achiever.score ?? 0;
                        return (
                        <tr key={achiever.id || `achiever-${rank}`} className={rank <= 3 ? 'top-three' : ''}>
                          <td className="rank-cell">
                            <span className="rank-num">#{rank}</span>
                            {rank === 1 && <span className="rank-badge gold">Gold</span>}
                            {rank === 2 && <span className="rank-badge silver">Silver</span>}
                            {rank === 3 && <span className="rank-badge bronze">Bronze</span>}
                          </td>
                          <td className="name-cell">{achiever.student_name || 'Unknown'}</td>
                          <td>{achiever.game_student_id || 'Not linked'}</td>
                          <td>{achiever.grade_level || 'N/A'}</td>
                          <td>{achiever.section || 'Not assigned'}</td>
                          <td className="progress-cell">
                            <div className="progress-bar">
                              {completionWidth !== null && (
                                <div className="progress-fill" style={{ width: `${completionWidth}%` }} />
                              )}
                            </div>
                            <span className="progress-text">{formatCompletionPercent(completion)}</span>
                          </td>
                          <td className="game-score-cell">{formatMetric(gameScore)}</td>
                          <td>{formatMetric(achiever.total_correct_answers ?? achiever.correct_answers)}/{formatMetric(achiever.total_questions_answered ?? achiever.total_questions)}</td>
                          <td>{formatMetric(achiever.quests_completed ?? achiever.total_quests_completed)}</td>
                          <td>{formatPlaytime(achiever.total_play_time ?? achiever.duration_seconds)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <div className="pagination-row no-print" aria-label="Top Achievers pagination">
                    <span>{paginatedAchievers.totalItems === 0 ? '0 records' : `Showing ${paginatedAchievers.start} - ${paginatedAchievers.end} of ${paginatedAchievers.totalItems} records`}</span>
                    <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={paginatedAchievers.currentPage === 1}>Previous</button>
                    <span>Page {paginatedAchievers.currentPage} of {paginatedAchievers.totalPages}</span>
                    <button type="button" onClick={() => setPage((current) => Math.min(paginatedAchievers.totalPages, current + 1))} disabled={paginatedAchievers.currentPage === paginatedAchievers.totalPages}>Next</button>
                  </div>
                </>
              )}
              <PrintableTableReport
                title="Top Achievers Report"
                context={reportScope}
                rows={filteredAchievers}
                columns={reportColumns}
              />
            </ContentSection>
          </PageContent>
        </MainContent>
      }
    />
  );
}
